import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";
import { field } from "@/components/site/AuthForm";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data);
      toast.success("Password updated. You're signed in.");
      nav(data?.role === "admin" ? "/admin" : "/my-orders");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid place-items-center px-5 relative" data-testid="reset-page">
      <div className="absolute inset-0 grid-floor opacity-60" />
      <div className="relative glass rounded-[28px] p-8 sm:p-10 max-w-md w-full">
        <a href="/" className="inline-block"><LogoMark className="h-10 w-10 mb-6" /></a>
        <h1 className="font-display text-2xl font-bold mb-2">Choose a new password</h1>
        {!token ? (
          <p data-testid="reset-missing-token" className="text-slate-300 text-sm">
            This reset link is incomplete. <a href="/login" className="text-[#d5d9df] hover:text-white">Request a new one</a>.
          </p>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <p className="text-slate-400 text-sm mb-8">Use at least 8 characters.</p>
            <div className="space-y-3">
              <input data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className={field} />
              <input data-testid="reset-confirm-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={field} />
            </div>
            {error && (
              <p data-testid="reset-error" className="text-red-400 text-sm mt-4">
                {error} {error.includes("expired") && <a href="/login" className="text-[#d5d9df] hover:text-white">Request a new link</a>}
              </p>
            )}
            <button data-testid="reset-submit" disabled={busy} className="btn-primary w-full justify-center py-4 mt-6">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
